import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';

const Footer = () => {
  const { t } = useTranslation();

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  const scrollRevealVariants = {
    hidden: { 
      opacity: 0,
      y: 50
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.footer 
      className="bg-white border-t border-gray-100"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={scrollRevealVariants}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Company info */}
          <motion.div 
            className="lg:col-span-2"
            variants={itemVariants}
          >
            <Link to="/" className="block mb-6">
              <img 
                src="https://res.cloudinary.com/djimcqajg/image/upload/v1741340483/vefsida-logo-v2_y5imtb.svg" 
                alt="Vefsíða" 
                className="h-8 w-auto"
              />
            </Link>
            <p className="mt-4 text-gray-600 max-w-md">
              {t('site.description')}
            </p>
            <div className="mt-6 flex space-x-4">
              <motion.a 
                href="https://www.facebook.com/vefsida" 
                className="text-gray-400 hover:text-[#EA295A] transition-colors"
                whileHover={{ scale: 1.2 }}
              >
                <Facebook className="w-5 h-5" />
              </motion.a>
              <motion.a 
                href="https://x.com/vefsida" 
                className="text-gray-400 hover:text-[#EA295A] transition-colors"
                whileHover={{ scale: 1.2 }}
              >
                <Twitter className="w-5 h-5" />
              </motion.a>
              <motion.a 
                href="https://www.instagram.com/vefsida" 
                className="text-gray-400 hover:text-[#EA295A] transition-colors"
                whileHover={{ scale: 1.2 }}
              >
                <Instagram className="w-5 h-5" />
              </motion.a>
              <motion.a 
                href="https://www.linkedin.com/company/vefsida" 
                className="text-gray-400 hover:text-[#EA295A] transition-colors"
                whileHover={{ scale: 1.2 }}
              >
                <Linkedin className="w-5 h-5" />
              </motion.a>
            </div>
          </motion.div>

          {/* Contact info */}
          <motion.div variants={itemVariants}>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              {t('common.contact')}
            </h3>
            <div className="space-y-3">
              <motion.div 
                className="flex items-center text-gray-600"
                whileHover={{ x: 5 }}
              >
                <Phone className="w-5 h-5 text-[#EA295A] mr-3" />
                <span>(+354) 537-7990</span>
              </motion.div>
              <motion.div 
                className="flex items-center text-gray-600"
                whileHover={{ x: 5 }}
              >
                <Mail className="w-5 h-5 text-[#EA295A] mr-3" />
                <span>hallo@vefsida.is</span>
              </motion.div>
              <motion.div 
                className="flex items-center text-gray-600"
                whileHover={{ x: 5 }}
              >
                <MapPin className="w-5 h-5 text-[#EA295A] mr-3" />
                <span>Reykjavík, Kaupmannahöfn, Barcelona</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Quick links */}
          <motion.div variants={itemVariants}>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              {t('common.quickLinks')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/thjonusta" className="text-gray-600 hover:text-[#EA295A] transition-colors">
                  {t('common.services')}
                </Link>
              </li>
              <li>
                <Link to="/lausnir" className="text-gray-600 hover:text-[#EA295A] transition-colors">
                  {t('common.solutions')}
                </Link>
              </li>
              <li>
                <Link to="/verdskra" className="text-gray-600 hover:text-[#EA295A] transition-colors">
                  {t('nav.pricing')}
                </Link>
              </li>
              <li>
                <Link to="/frettir" className="text-gray-600 hover:text-[#EA295A] transition-colors">
                  {t('nav.news')}
                </Link>
              </li>
              <li>
                <Link to="/hafa-samband" className="text-gray-600 hover:text-[#EA295A] transition-colors">
                  {t('common.contact')}
                </Link>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <motion.div 
          className="mt-12 pt-8 border-t border-gray-100"
          variants={scrollRevealVariants}
        >
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm">
              © 2024 Vefsíða. Rekið af Novamedia ehf. | Kt. 540606-2260
            </p>
            <div className="mt-4 md:mt-0 flex flex-wrap justify-center gap-6">
              <motion.div whileHover={{ scale: 1.05 }}>
                <Link 
                  to="/skilmalar" 
                  className="text-gray-500 hover:text-[#EA295A] text-sm transition-colors"
                >
                  Skilmálar
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Link 
                  to="/personuvernd" 
                  className="text-gray-500 hover:text-[#EA295A] text-sm transition-colors"
                >
                  Persónuvernd
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Link 
                  to="/vafrakokur" 
                  className="text-gray-500 hover:text-[#EA295A] text-sm transition-colors"
                >
                  Vafrakökur
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;